import React, { useState, useMemo } from 'react';
import { View, Text, Pressable, ScrollView } from 'react-native';
import {
  LayoutGrid,
  List,
  LayoutList,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import AnimatedPressable from '@/components/AnimatedPressable';
import { webStyle } from '@/lib/platform';

export type ListLayoutType = 'grid' | 'list' | 'compact';

interface ListLayoutProps<T> {
  items: T[];
  renderItem: (item: T, index: number, layout: ListLayoutType) => React.ReactNode;
  keyExtractor: (item: T) => string;
  layout?: ListLayoutType;
  onLayoutChange?: (layout: ListLayoutType) => void;
  showLayoutOptions?: boolean;
  pageSize?: number;
  showPagination?: boolean;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;
  isDesktop?: boolean;
}

export function ListLayout<T>({
  items,
  renderItem,
  keyExtractor,
  layout: controlledLayout,
  onLayoutChange,
  showLayoutOptions = true,
  pageSize = 10,
  showPagination = true,
  emptyMessage = 'No items found',
  emptyIcon,
  isDesktop = false,
}: ListLayoutProps<T>) {
  const colors = useThemeColors();
  const [internalLayout, setInternalLayout] = useState<ListLayoutType>('list');
  const [currentPage, setCurrentPage] = useState(1);

  const layout = controlledLayout ?? internalLayout;
  const handleLayoutChange = (newLayout: ListLayoutType) => {
    if (onLayoutChange) {
      onLayoutChange(newLayout);
    } else {
      setInternalLayout(newLayout);
    }
    setCurrentPage(1); // Reset to first page on layout change
  };

  // Pagination
  const totalPages = Math.ceil(items.length / pageSize);
  const paginatedItems = useMemo(() => {
    if (!showPagination) return items;
    const start = (currentPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, currentPage, pageSize, showPagination]);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Get layout-specific styles
  const getContainerStyle = () => {
    switch (layout) {
      case 'grid':
        return isDesktop ? 'flex-row flex-wrap' : 'flex-row flex-wrap';
      case 'compact':
        return '';
      default:
        return '';
    }
  };

  const getItemStyle = (index: number) => {
    switch (layout) {
      case 'grid':
        return isDesktop
          ? webStyle({ marginBottom: 16 }, { width: 'calc(33.333% - 11px)' })
          : { width: '48%' as const, marginBottom: 12 };
      case 'compact':
        return { marginBottom: 8 };
      default:
        return { marginBottom: 16 };
    }
  };

  const layoutOptions: { value: ListLayoutType; icon: typeof LayoutGrid; label: string }[] = [
    { value: 'grid', icon: LayoutGrid, label: 'Grid' },
    { value: 'list', icon: LayoutList, label: 'List' },
    { value: 'compact', icon: List, label: 'Compact' },
  ];

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);

      if (currentPage > 3) pages.push('...');

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }

      if (currentPage < totalPages - 2) pages.push('...');

      if (!pages.includes(totalPages)) pages.push(totalPages);
    }

    return pages;
  };

  return (
    <View>
      {/* Layout Options & Results Count */}
      {showLayoutOptions && (
        <View className="flex-row items-center justify-between mb-4">
          <Text style={{ color: colors.text.secondary }} className="text-sm">
            {items.length} result{items.length !== 1 ? 's' : ''}
            {showPagination && totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
          </Text>

          <View className="flex-row items-center gap-1 p-1 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
            {layoutOptions.map((option) => {
              const Icon = option.icon;
              const isActive = layout === option.value;
              return (
                <AnimatedPressable
                  key={option.value}
                  onPress={() => handleLayoutChange(option.value)}
                  className="p-2 rounded-md"
                  style={{
                    backgroundColor: isActive ? colors.surface.primary : 'transparent',
                  }}
                >
                  <Icon
                    size={18}
                    color={isActive ? colors.primary[600] : colors.text.tertiary}
                  />
                </AnimatedPressable>
              );
            })}
          </View>
        </View>
      )}

      {/* Items */}
      {paginatedItems.length > 0 ? (
        <View className={getContainerStyle()} style={layout === 'grid' ? { gap: isDesktop ? 16 : 12, justifyContent: 'space-between' } : {}}>
          {paginatedItems.map((item, index) => (
            <Animated.View
              key={keyExtractor(item)}
              entering={FadeIn.delay(index * 30).duration(200)}
              style={getItemStyle(index)}
            >
              {renderItem(item, index, layout)}
            </Animated.View>
          ))}
        </View>
      ) : (
        <View className="items-center py-12">
          {emptyIcon}
          <Text style={{ color: colors.text.tertiary }} className="mt-4 text-center">
            {emptyMessage}
          </Text>
        </View>
      )}

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <View className="flex-row items-center justify-center gap-2 mt-6 pt-4 border-t" style={{ borderColor: colors.border.light }}>
          <AnimatedPressable
            onPress={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-lg"
            style={{
              backgroundColor: currentPage === 1 ? colors.surface.secondary : colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
              opacity: currentPage === 1 ? 0.5 : 1,
            }}
          >
            <ChevronLeft size={18} color={colors.text.secondary} />
          </AnimatedPressable>

          {getPageNumbers().map((page, index) => (
            typeof page === 'number' ? (
              <AnimatedPressable
                key={index}
                onPress={() => goToPage(page)}
                className="w-9 h-9 rounded-lg items-center justify-center"
                style={{
                  backgroundColor: currentPage === page ? colors.primary[600] : colors.surface.primary,
                  borderWidth: currentPage === page ? 0 : 1,
                  borderColor: colors.border.light,
                }}
              >
                <Text
                  style={{ color: currentPage === page ? 'white' : colors.text.secondary }}
                  className="font-medium text-sm"
                >
                  {page}
                </Text>
              </AnimatedPressable>
            ) : (
              <Text key={index} style={{ color: colors.text.tertiary }} className="px-1">
                {page}
              </Text>
            )
          ))}

          <AnimatedPressable
            onPress={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg"
            style={{
              backgroundColor: currentPage === totalPages ? colors.surface.secondary : colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
              opacity: currentPage === totalPages ? 0.5 : 1,
            }}
          >
            <ChevronRight size={18} color={colors.text.secondary} />
          </AnimatedPressable>
        </View>
      )}
    </View>
  );
}

// Hook to manage list layout state
export function useListLayout(defaultLayout: ListLayoutType = 'list') {
  const [layout, setLayout] = useState<ListLayoutType>(defaultLayout);
  return { layout, setLayout };
}
